"use client";

import { useEffect, useRef, type CSSProperties } from "react";
import Image from "next/image";
import ScrollReveal from "@/components/common/ScrollReveal";
import BarHeading from "@/components/common/BarHeading";
import OTKeyFeatures from "./OTKeyFeatures";

interface SectionData {
  label: string;
  title: string;
  paragraphs: string[];
  features: string[];
}

const testbed: SectionData = {
  label: "Testbed",
  title: "Real hardware. Real behavior.",
  paragraphs: [
    "The Testbed is the physical foundation of the OT Cyber Range which connects actual Level 0 field devices (sensors, pumps, valves, meters) to a miniature model that replicates industrial plants.",
    "Real PLCs from the leading vendors run the control logic, giving engineers and operators hands-on experience with the exact hardware they work with in production. What happens here is grounded in physics, not software approximation.",
  ],
  features: [
    "Sensor Integration for Real-Time Data Monitoring",
    "Pump Simulation for Operational Understanding",
    "Valve Control Simulation for Fluid Flow Management",
    "Metering System Simulation for Process Optimization",
    "PLC-Based Automation — Siemens S7-1200, Mitsubishi MELSEC Q series, Schneider Electric Modicon M340",
    "HVAC System Control — ABB AC500 series, Honeywell HC900 Hybrid Control System",
    "Water Treatment Plant Control — Hitachi EH-150, Yokogawa STARDOM, GE Fanuc VersaMax",
    "Energy Grid Management — Siemens S7-1500, ABB AC500 series, Schneider Electric Modicon Quantum",
    "Multiple quadrants: City, Industrial, Oil and Gas, Wind/Power, Water, Transport, Military",
    "Serial / USB / WiFi enabled control device",
  ],
};

const otLab: SectionData = {
  label: "OT Lab",
  title: "Your control environment, fully virtualized.",
  paragraphs: [
    "The OT Lab virtualizes everything your team interacts with at the supervisory and enterprise level, including HMI screens, engineering workstations, historian systems, databases, and the full security stack.",
    "Configurations, security settings, and patch levels are imported directly from your live environment, OT Lab reflects the actual state of your infrastructure. This is where security testing, incident response training, and vulnerability assessment happen: contained, repeatable, and safe.",
  ],
  features: [
    "Human Machine Interface (HMI) Virtualization — Aveva, Inductive Automation",
    "Engineering Station Virtualization — Siemens, Yokogawa",
    "Historian Virtualization — Honeywell",
    "Database Virtualization — PostgreSQL, IBM, Oracle, Microsoft",
    "Configuration Replication — Security Settings, Patch Levels, Network Configuration",
    "Firewall Configuration Testing",
    "Intrusion Detection System (IDS) Simulation",
    "SIEM System Response Training",
    "Endpoint Protection Evaluation",
    "Security Incident Response Practice",
  ],
};

const ART = "/images/OTSecurity/cyber-range";

interface RangeView {
  id: string;
  src: string;
  /** Screen-reader description. Never rendered as a caption: the photos are
   *  shown unnamed, so the page puts none of our words on them. */
  alt: string;
  /** Centre of this view's target ring as a % of the radar box. */
  x: number;
  y: number;
}

/** The artwork's own units. The box and the artwork are both 2502x1950 and the
 *  image is `object-contain`, so it fills the box exactly and units convert to
 *  percentages of the box with nothing left over. */
const BOX = { w: 2502, h: 1950 };

/** Centre the travelling line turns about — and it is NOT the dial centre. The hub
 *  dot, the thick grey ring and the dotted ring all fit 1177.9, 1163.3, but this
 *  circle is struck from 9.8px down-right of that: turned about the hub its outer
 *  edge wanders 564.5..578.1, a 13.6px precession once per revolution, while
 *  turned about this centre it holds 573.8..575.1 and so turns in place. Fitted
 *  by scripts/build-hero-radar-still.mjs, which searches for the centre that
 *  minimises the variance of the line's pixel radius. */
const SPIN = { x: 1187.5, y: 1161.55 };
const SPIN_ORIGIN = `${((SPIN.x / BOX.w) * 100).toFixed(3)}% ${((SPIN.y / BOX.h) * 100).toFixed(3)}%`;

/** Seconds per revolution, and the only clock on this dial. EVERYTHING runs one
 *  animation of exactly this length — the line, the three ring glows, the three
 *  pings and the three photographs — so they are locked together for the life of
 *  the page with no timer to drift. It also sets the photo cadence: the rings are
 *  74, 127 and 159 degrees apart, so at 20s a photograph holds 4-9s. */
const SPIN_PERIOD = 20;


/** The line comes in two pieces: a 211deg dark run and a 58.15deg red one at
 *  36.02..94.17. The red piece is the marker — this is its leading end, so turning
 *  clockwise it is what arrives at each static target ring in turn, and the ring is
 *  lit for exactly as long as the red piece is over it. Its 58.15deg width is the
 *  lit window, and it can only live in the stylesheet as a literal (see the note on
 *  that block), so it is not a constant here. */
const LEAD = 94.17;

/** Half-size of a target ring (they are 79px across on a 2502x1950 canvas), plus
 *  a 6px margin, as a % of each axis — the clip window for its glow. */
const RING_HALF = { x: ((39.5 + 6) / BOX.w) * 100, y: ((39.5 + 6) / BOX.h) * 100 };

/** Photographs of the physical range, one per target ring the artwork threads on
 *  the line, listed in the order the red piece reaches them — which is the order
 *  the hold percentages in the stylesheet are written in, so the two lists have to
 *  stay aligned. build-hero-radar-still.mjs throws if they drift apart.
 *
 *  Centres come from the artwork itself: that script finds the three ring
 *  interiors as the only enclosed holes in the band and checks these values
 *  against them, which they match to 1.6px.
 *
 *  `alt` is for screen readers only — nothing on the page names the photos,
 *  since any such name would be ours rather than the range's. */
const RANGE_VIEWS: RangeView[] = [
  {
    id: "overview",
    src: `${ART}/testbed-range-overview.jpg`,
    alt: "The physical OT Cyber Range model lit at night — a smart-city district, a harbour with an offshore platform, and the refinery skids running along the front of the table",
    x: 44.7,
    y: 88.6,
  },
  {
    id: "cabinet",
    src: `${ART}/testbed-control-cabinet.png`,
    alt: "The control cabinet driving the range — an HMI screen and real PLCs racked behind glass, standing between two process panels with lit tanks, cooling towers and an LNG sphere",
    x: 41.9,
    y: 31.2,
  },
  {
    id: "grid",
    src: `${ART}/testbed-city-grid-aerial.jpg`,
    alt: "The range from above — the lit city district and its tower at one end, the process plant's tanks and blue pipe racks at the other, and the substation's lattice towers and live-lit lines along the front edge",
    x: 67.1,
    y: 45.0,
  },
];

/** Bearing of a view's ring, degrees clockwise from 12 o'clock about SPIN. The
 *  rings never move, so this is fixed. The two percentages are of different axes,
 *  so they go back to artwork units first or the box's 2502x1950 shape would skew
 *  the angle. */
const bearingOf = (v: RangeView) => {
  const dx = (v.x / 100) * BOX.w - SPIN.x;
  const dy = (v.y / 100) * BOX.h - SPIN.y;
  return (Math.atan2(dx, -dy) * 180) / Math.PI;
};

/** Each layer runs the same keyframes and is phase-shifted onto its own ring by a
 *  negative delay, so no keyframe has to be written twice or wrapped past 100%. A
 *  delay of -D makes the animation behave as if it had started D seconds ago, and
 *  this is the D that puts progress 0 at the moment the red piece's leading end,
 *  which starts the page at LEAD, comes round to this ring. Delays go in through
 *  inline style, which is why they can stay computed while the stylesheet below
 *  cannot — see the note on that block. */
const phaseSeconds = (v: RangeView) => {
  const turned = (((bearingOf(v) - LEAD) % 360) + 360) % 360;
  return -(SPIN_PERIOD * (1 - turned / 360));
};

/** Every layer's delay: its own ring's phase, plus however far the controls have
 *  wound the dial. The shift arrives as a custom property rather than a computed
 *  number so that winding the dial is ONE property write on the root instead of a
 *  React render touching sixteen inline styles — which is what makes the wind a
 *  smooth travel rather than a jump. --range-spin is 0s until something picks,
 *  so the page renders on the server and hydrates on the frame it always did. */
const delayOf = (v: RangeView) =>
  `calc(${phaseSeconds(v).toFixed(3)}s + var(--range-spin))`;

/** How long a wind takes: the shortest hops are quick and a half-turn is still
 *  under a second, so the line always reads as travelling rather than seeking. */
const WIND_MIN_MS = 260;
const WIND_MAX_MS = 820;

/** Ease in and out of the wind. The line is already turning at a steady rate
 *  when a wind starts, so a curve that begins and ends at zero extra speed is
 *  what keeps the join invisible at both ends. */
const easeInOut = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

/** Settle a finished wind into [-period, 0). Whole periods make no difference to
 *  where an animation is, so this changes nothing on screen, but it keeps every
 *  delay negative for good: phase is already negative, and a delay that went
 *  POSITIVE past the animation's own currentTime would put that layer in its
 *  before-phase, where fill-mode none draws no keyframe at all and the layer
 *  simply vanishes. Winds walk the spin by up to half a period at a time, so
 *  without this it is a random walk with nothing keeping it in range. */
const wrapSpin = (s: number) =>
  (((s % SPIN_PERIOD) + SPIN_PERIOD) % SPIN_PERIOD) - SPIN_PERIOD;

/** The spin that puts ring `v` under the line's red piece RIGHT NOW.
 *
 *  A CSS animation's progress runs on its ACTIVE time, which is its currentTime
 *  minus its delay — and currentTime is measured from the moment the animation
 *  began, not from any absolute clock. So the ask "put this ring at progress 0"
 *  is delay = currentTime, and since delay is phase + spin, the spin falls out
 *  as currentTime - phase. Reading the running animation is the whole trick: a
 *  first version computed this from document.timeline.currentTime instead,
 *  which is the same clock the timeline runs on but a different ORIGIN, and
 *  every click landed a ring early.
 *
 *  `anim` is the turning line's animation, and any of this dial's would do — they
 *  were all created in the same commit and share a start time, so they share a
 *  currentTime too. It has to be an ANIMATION and not merely the first running
 *  effect on some element: an earlier version read it off the clicked control's
 *  own getAnimations()[0], which on a just-clicked dot is its hover TRANSITION,
 *  only milliseconds old, and the spin came out as if the dial had only just
 *  started. That reads as correct on a freshly opened page, where the two clocks
 *  are still close, and drifts further out of true the longer the page has been
 *  up. Reading one known layer instead of whatever the click landed on also
 *  means a control does not have to be animated itself to work — which is what
 *  lets the radar's own targets be buttons.
 *
 *  Nothing restarts. Changing animation-delay re-times a running animation
 *  rather than replaying it, and the turn, the glow, the ping and the photo all
 *  move together because they all take the same shift. Taken modulo the period
 *  so the number stays small however long the page has been open.
 *
 *  This is the DESTINATION, not a jump to it — `wind` walks the dial here over
 *  the best part of a second. Applying it in one go is what the first version
 *  did and the line lurched. */
const spinFor = (v: RangeView, anim: Animation | undefined) => {
  const ct = Number(anim?.currentTime ?? 0) / 1000;
  const raw = ct - phaseSeconds(v);
  return ((raw % SPIN_PERIOD) + SPIN_PERIOD) % SPIN_PERIOD;
};

/** The element's own keyframe animation, picked out of everything running on it.
 *  Duck-typed on animationName rather than `instanceof CSSAnimation`, which not
 *  every engine exposes as a global. */
const keyframeAnimation = (el: Element) =>
  el
    .getAnimations()
    .find(
      (a) => typeof (a as { animationName?: string }).animationName === "string",
    );

/** `clip-path` isolating one ring so only that ring glows. Derived from the
 *  ring's own centre, never typed by hand. */
const clipFor = (v: RangeView) =>
  `inset(${(v.y - RING_HALF.y).toFixed(3)}% ${(100 - v.x - RING_HALF.x).toFixed(3)}% ${(100 - v.y - RING_HALF.y).toFixed(3)}% ${(v.x - RING_HALF.x).toFixed(3)}%)`;

type Vars = CSSProperties & Record<string, string | number>;

const FeatureBullets = ({ items }: { items: string[] }) => {
  const mid = Math.ceil(items.length / 2);
  const cols = [items.slice(0, mid), items.slice(mid)];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
      {cols.map((col, ci) => (
        <ul key={ci} className="flex flex-col gap-1">
          {col.map((item, i) => (
            <li key={i} className="flex items-start gap-4">
              <span
                aria-hidden
                className="shrink-0 mt-[10px] block h-2 w-2 rounded-full bg-primary"
              />
              <p className="flex-1 min-w-0 text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
                {item}
              </p>
            </li>
          ))}
        </ul>
      ))}
    </div>
  );
};

const OverviewSection = ({ data }: { data: SectionData }) => (
  <div className="flex flex-col gap-8">
    <BarHeading label={data.label}>{data.title}</BarHeading>
    <div className="flex flex-col gap-6 sm:gap-7 text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
      {data.paragraphs.map((p, i) => (
        <p key={i}>{p}</p>
      ))}
    </div>
    <div className="flex flex-col gap-4">
      <p className="uppercase text-text-highlight font-semibold text-[14px] sm:text-[16px] tracking-[0.5px]">
        Features
      </p>
      <FeatureBullets items={data.features} />
    </div>
  </div>
);

/** Intro row — copy and the three range photos on the left, the radar on the
 *  right (Figma node 1824:42690). Per design the paragraph and the radar are two
 *  ~640px columns sharing the row, both starting at the top of the row, so the
 *  copy sits right under the tabs instead of floating in the middle of the
 *  499px-tall radar. They flex down together on narrower screens (each capped at
 *  640px) so the artwork always stays fully visible instead of being pushed out
 *  and clipped — the earlier fixed-640 + overflow-hidden setup cut the radar off
 *  on laptop widths below ~1360px.
 *
 *  One line on the dial travels. It is the middle circle's stroke — a long dark
 *  run and a short red one — and it turns clockwise once every SPIN_PERIOD. The
 *  dial carries no sweeping wedge behind it: a lit conic sector used to sit under
 *  the rim and it is gone, so the artwork's own colour is all there is. The three
 *  hollow red target rings the artwork threads on the middle circle stay exactly
 *  where they are drawn and the line runs under them, so the red piece arrives at
 *  each ring in turn, and while it is over one that ring lights up and the
 *  photograph under the copy becomes the one belonging to it. Nothing is explained
 *  in words and the photos go unnamed — any name would be ours rather than the
 *  range's — so their descriptions live only in `alt`.
 *
 *  Every moving part is one CSS animation of the same length started at the same
 *  moment, phase-shifted onto its own ring by a negative delay, so the turn, the
 *  glows, the pings and the photographs are locked together for the life of the
 *  page. That also means no timer to drift: an earlier version of this section
 *  toggled animated classes on and off and the phases collapsed the first time
 *  anyone interacted with it.
 *
 *  The dots under the photograph and the three rings on the radar can wind that
 *  clock — they add a shift to every delay at once, so the line TRAVELS round to
 *  the ring picked, carrying the glow, the ping and the photograph with it. They
 *  never take the dial out of its own logic; they only move where it is up to.
 *
 *  Nothing pulses on its own. A ping IS the arrival of the line, not a decoration
 *  parked on the ring: each marker is dark until the red piece reaches its ring,
 *  fires while the piece is over it, and goes dark as the piece leaves. A version
 *  that gave the three markers their own short beat was rejected — with all three
 *  blinking all of the time nothing distinguished the ring the line had actually
 *  reached.
 *
 *  The trade this makes: content that updates itself with no way to pause it is
 *  WCAG 2.2.2 territory. `prefers-reduced-motion` parks the whole dial on a
 *  single coherent frame, which is the mitigation. */
const RangeIntro = () => {
  /* How far the controls have wound the dial, in seconds. It holds a TIME rather
     than an index on purpose: the dial keeps running the same CSS animations it
     always did and this only re-times them, so picking cannot collapse the phase
     relationship between the turn, the glows, the pings and the photographs the
     way a version that toggled classes on and off once did. Which dot reads as
     current is likewise still CSS — see the .range-dot rules — so nothing here
     has to track a timer.

     It lives in a ref and a custom property rather than in React state. A wind
     writes it sixty times a second, and going through state would re-render the
     whole section on every one of those frames to reach sixteen inline styles;
     this way each frame is a single property write on the root and the browser
     re-times the animations from there. */
  const rootRef = useRef<HTMLDivElement>(null);
  const spinRef = useRef(0);
  const windRef = useRef<number | null>(null);
  const setSpin = (s: number) => {
    spinRef.current = s;
    rootRef.current?.style.setProperty("--range-spin", `${s.toFixed(3)}s`);
  };

  /* The turning line is the dial's clock: every control reads its currentTime
     rather than its own, so nothing has to be animated to be clickable. */
  const turnRef = useRef<HTMLSpanElement>(null);

  useEffect(
    () => () => {
      if (windRef.current !== null) cancelAnimationFrame(windRef.current);
    },
    [],
  );

  /** Travel the dial round to `v`'s ring instead of cutting to it.
   *
   *  The way round is the SHORTEST one, which for these three rings is always
   *  the direct one: they sit 74, 127 and 159 degrees apart, all under a half
   *  turn, so the short way between any two never crosses the third. That is
   *  what keeps a wind from strobing a photograph it is only passing over.
   *
   *  The destination is where the ring wants to be when the wind ENDS, so the
   *  travel time is added back into it — otherwise the dial would arrive at
   *  where the ring was when the click happened and sit a wind-length behind. */
  const pick = (v: RangeView) => {
    const el = turnRef.current;
    const from = spinRef.current;
    const target = spinFor(v, el ? keyframeAnimation(el) : undefined);

    let d = (((target - from) % SPIN_PERIOD) + SPIN_PERIOD) % SPIN_PERIOD;
    if (d > SPIN_PERIOD / 2) d -= SPIN_PERIOD;

    if (windRef.current !== null) cancelAnimationFrame(windRef.current);

    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (still || Math.abs(d) < 0.01) {
      setSpin(wrapSpin(target));
      return;
    }

    const ms =
      WIND_MIN_MS +
      (Math.abs(d) / (SPIN_PERIOD / 2)) * (WIND_MAX_MS - WIND_MIN_MS);
    d += ms / 1000;

    const t0 = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - t0) / ms);
      if (t < 1) {
        setSpin(from + d * easeInOut(t));
        windRef.current = requestAnimationFrame(step);
      } else {
        setSpin(wrapSpin(from + d));
        windRef.current = null;
      }
    };
    windRef.current = requestAnimationFrame(step);
  };

  return (
    /* The period and the wind are single-sourced here as custom properties. The
       stylesheet at the bottom has to be static (see the note there), and this is
       the one way to keep a computed number in it without interpolating into the
       CSS. They have to sit above BOTH columns: the photographs are in the left
       one and everything else is in the radar on the right. */
    <div
      ref={rootRef}
      style={{ "--range-period": `${SPIN_PERIOD}s`, "--range-spin": "0s" } as Vars}
      className="flex flex-col gap-8 lg:gap-10"
    >
      <p className="text-text-highlight text-[14px] sm:text-[15px] lg:text-[16px] leading-[24px] sm:leading-[26px] lg:leading-[28px] tracking-[0.5px]">
        Built in partnership with <span className="font-bold">SkillSpar</span>, a
        specialist in OT simulation and cyber training infrastructure, OT Cyber
        Range simulates your actual industrial environment end-to-end — every
        PLC, every configuration, every security control, every network topology
        giving your team the experience to defend it before a real threat demands
        it.
      </p>

      {/* Both visuals share one panel, the same one the HackGrid product page
          builds around its wheel (Products/HackgridProduct.tsx): 16px radius,
          #3F444A hairline, the plate texture and the offset shadow. Like that one
          it is `lg:`-only — on a phone the border, fill, texture and shadow all
          drop and the two visuals sit on the page, which is also why the padding
          is `lg:` only.
          The one departure is the fill: pure black instead of HackGrid's
          #111113 -> #1d1e20 gradient, because all three photographs were shot on
          a black backdrop — sampling their edges gives rgb(0,0,1), rgb(1,1,1)
          and a median luma of 6.9 — so black is what lets a photo's border
          dissolve into the panel instead of reading as a pasted rectangle. */}
      <div className="relative w-full min-w-0 overflow-hidden flex flex-col lg:rounded-[16px] lg:border lg:border-[#3F444A] lg:bg-black lg:drop-shadow-[-16px_21px_14px_rgba(0,0,0,0.3)]">
        {/* `priority` because the panel sits high on this page, which makes this
            1300x525 decoration the largest above-the-fold paint and therefore
            the LCP element — Next.js warns about it on every desktop load
            otherwise. On the HackGrid page the same texture is further down and
            needs no such hint. */}
        <Image
          src="/images/Product/hackgrid_panel_texture.png"
          alt=""
          fill
          aria-hidden
          priority
          sizes="(max-width: 1024px) 90vw, 1300px"
          className="hidden lg:block absolute inset-0 object-cover object-bottom opacity-35 pointer-events-none select-none"
        />

        <div className="relative flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-10 lg:px-10 lg:py-8">
          <div className="w-full lg:flex-1 lg:min-w-0 lg:max-w-[640px]">
            {/* The photo belonging to whichever target ring the line's red piece
                is over. Nothing to frame — the panel around it is the frame. All
                three stay mounted and swap on opacity rather than being keyed and
                remounted, since the sources are full-resolution photographs and a
                remount flashes empty on the first switch. The box is a fixed 3/2
                so the column height never moves — all three sources are 3/2 to
                within 0.2%, so `object-cover` has all but nothing to trim.

                No live region: the photo changes on its own every 4-9s, so
                announcing each one would only talk over the reader. */}
            <div className="relative w-full aspect-[3/2] overflow-hidden">
              {RANGE_VIEWS.map((view, i) => (
                <span
                  key={view.id}
                  className={`range-photo range-photo-${i} absolute inset-0 block`}
                  style={{ animationDelay: delayOf(view) }}
                >
                  <Image
                    src={view.src}
                    alt={view.alt}
                    fill
                    sizes="(max-width: 1024px) 92vw, (max-width: 1360px) 45vw, 640px"
                    className="object-cover"
                  />
                </span>
              ))}
            </div>

            {/* Slider dots, the same ones the HackGrid product panel carries
                under its wheel (Products/HackgridProduct.tsx): an 8px track dot
                per view, the current one stretched to 24px and turned red.

                What differs is where "current" comes from. HackGrid holds a slide
                index in React; here the photographs are driven by the dial's CSS
                clock, so a React index would need a timer beside it and the two
                would drift. Each dot instead runs its OWN copy of its photo's
                keyframes — same delay, same period — so it is wide and red for
                exactly as long as that photo is up, with nothing to keep in sync.

                Clicking one winds the whole dial to that ring rather than just
                swapping the picture: the line cuts round to it, its ring lights,
                its ping fires, and the photo changes with them. The three ring
                targets on the radar itself do exactly the same thing, through
                the same `pick`.

                Three things are deliberately NOT here. No autoplay pause: the
                dial is this section's clock and stopping it would strand the
                radar mid-turn. No aria-current: which dot is current is a fact
                about a CSS animation that React genuinely does not know, so each
                dot says which view it goes to instead. And the whole row is
                hidden under prefers-reduced-motion, where the dial is parked on
                one frame with one photograph — there is no sequence left to step
                through, and a control that cannot change anything is worse than
                no control. */}
            <div className="mt-5 flex items-center justify-center gap-3 motion-reduce:hidden">
              {RANGE_VIEWS.map((view, i) => (
                <button
                  key={`${view.id}-dot`}
                  type="button"
                  onClick={() => pick(view)}
                  aria-label={`Show range view ${i + 1} of ${RANGE_VIEWS.length}`}
                  style={{ animationDelay: delayOf(view) }}
                  className={`range-dot-${i} h-2 w-2 shrink-0 cursor-pointer rounded-full bg-neutral-500 transition-[filter] hover:brightness-150`}
                />
              ))}
            </div>
          </div>

          {/* Capped and centred below `lg`: at full phone width the labelled radar
              renders its 640px-design labels at ~6px, so the artwork is pinned to a
              readable size instead. `lg:mx-0` keeps the two-column row exactly as
              it was — the auto margins would otherwise sit alongside `lg:flex-1`. */}
          <div className="relative w-full max-w-[320px] mx-auto sm:max-w-[480px] lg:mx-0 lg:flex-1 lg:min-w-0 lg:max-w-[640px] aspect-[2502/1950]">
            {/* The travelling line, and the only thing on this dial that moves.
                The export is a flattened Figma bitmap, so it had to be cut off the
                pixels first, and cut again to leave the three target rings behind:
                scripts/build-hero-radar-still.mjs splits the band along the rings,
                rebuilds the six notches their strokes had punched out of the line,
                and proves the split by compositing the two files and demanding
                hero-radar.png back, to the channel.

                It goes UNDER the still layer, which is where the artwork puts it —
                the rings are drawn on top of the line — and nothing else in that
                layer comes anywhere near this band's radii, so nothing else is
                affected by the order.

                The animation is reached through this wrapper span rather than put
                on the Image: styled-jsx scopes its rules by stamping a hash class
                onto JSX elements, and Image is a component rather than a DOM tag,
                so a rule aimed at one would silently never apply. */}
            <span
              ref={turnRef}
              aria-hidden="true"
              style={{ transformOrigin: SPIN_ORIGIN, animationDelay: "var(--range-spin)" }}
              className="range-turn absolute inset-0 z-[2] block pointer-events-none select-none"
            >
              <Image
                src={`${ART}/hero-radar-turn.png`}
                alt=""
                fill
                sizes="(max-width: 1024px) 90vw, (max-width: 1360px) 45vw, 640px"
                priority
                className="object-contain"
              />
            </span>

            {/* Everything that stays put, in one file: the skyline and its smoke,
                the hub, the dotted ring, the thick grey ring, the outer rim, and
                the three target rings lifted out of the turning band. One file
                rather than five because this sits above the fold and every extra
                layer is another request on the LCP path. */}
            <Image
              src={`${ART}/hero-radar-still.png`}
              alt="OT Cyber Range — industrial process radar visualization"
              fill
              sizes="(max-width: 1024px) 90vw, (max-width: 1360px) 45vw, 640px"
              priority
              className="z-[3] object-contain"
            />

            {/* One glow per target ring, and no new image for any of them: the
                layer is the SAME still artwork again, clipped to a window around
                that ring and given a red halo, so what lights up is the ring the
                illustration already drew rather than something painted over it.
                Re-rendering it costs no extra request, and the build script asserts
                each window holds that ring's pixels and nothing else at all.

                Two traps, both worth the words. `clip-path` has to be on the
                inner Image and `filter` on the wrapper — put both on one element
                and the drop-shadow is clipped into a rectangle. And there is no
                `brightness` in the filter on purpose: the source is a raster, so
                boosting it reveals the clip's straight edge. A red halo alone has
                no hard edge to give away. */}
            {RANGE_VIEWS.map((view, i) => (
              <span
                key={`${view.id}-glow`}
                aria-hidden="true"
                style={{ animationDelay: delayOf(view) }}
                className={`range-hit range-hit-${i} absolute inset-0 z-[4] block pointer-events-none select-none drop-shadow-[0_0_5px_rgba(236,25,56,0.85)]`}
              >
                <Image
                  src={`${ART}/hero-radar-still.png`}
                  alt=""
                  fill
                  sizes="(max-width: 1024px) 90vw, (max-width: 1360px) 45vw, 640px"
                  style={{ clipPath: clipFor(view) }}
                  className="object-contain saturate-[1.2]"
                />
              </span>
            ))}

            {/* The ping on each ring, and it is ONE mark: a 6px core in the
                middle of the ring that flickers twice as the line arrives.

                Three things the reference prototype draws are deliberately
                absent, and each was tried here first. Its four crosshair ticks
                are gone — on a dial already ruled with rings, dots and a rim
                that cross read as more instrument furniture, not as a signal.
                Its expanding ripple is gone — a mark that bursts open pulls the
                eye harder than the line whose arrival it reports. And its 22px
                ring outline is gone, which matters most: the artwork ALREADY
                draws a hollow ring here, 79px on a 2502px canvas, which lands
                at 18.6px when the radar renders at 589px. A 22px outline sits
                1.7px outside that, so the two read as one thick double-struck
                border, and an 11px core with a 3px shadow spread filled what
                was left. The mark looked like a button. What lights the ring
                itself is the glow layer above, which re-renders the artwork's
                own ring — so the only thing left to add is what the artwork
                does not have: a lit centre.

                The core rides the SAME range-hit window as that glow, which is
                why the wrapper carries the class: dark until the line's red
                piece reaches this ring, lit for the 3.23s the piece is over it,
                dark again after. That is the one thing it says — the line is
                HERE, now.

                The wrapper is a zero-size box pinned to the ring's centre, so
                the core centres on that exact point with -translate-1/2 and
                stays there at any radar size. It is sized in px rather than as
                a share of the radar because it is a mark laid over the artwork
                rather than part of it, and it still has to read when the dial
                is capped at 320px on a phone. */}
            {RANGE_VIEWS.map((view, i) => (
              <span
                key={`${view.id}-ping`}
                aria-hidden="true"
                style={{
                  left: `${view.x}%`,
                  top: `${view.y}%`,
                  animationDelay: delayOf(view),
                }}
                className={`range-hit range-hit-${i} absolute z-[5] block size-0 pointer-events-none select-none`}
              >
                <span
                  style={{ animationDelay: delayOf(view) }}
                  className="range-core absolute left-0 top-0 block size-[6px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary shadow-[0_0_7px_2px_rgba(236,25,56,0.8)]"
                />
              </span>
            ))}

            {/* The three targets, as buttons. Same job as a dot under the photo
                — wind the dial to this ring — reached by clicking the ring on
                the radar itself.

                They are SEPARATE from the ping above rather than wrapped around
                it, because the two have opposite lifetimes: the ping is dark for
                most of a revolution, and a control that is only clickable while
                it happens to be lit would be a trap. What a reader aims at is
                the artwork's own hollow ring, which never goes anywhere, so the
                target sits on that and stays live the whole time.

                44px because that is the touch minimum and the ring itself is far
                under it — 18.6px at a 589px radar and 10px at the 320px the dial
                is capped to on a phone. Nothing else on the dial is interactive,
                so covering that much of the artwork costs nothing, and the three
                rings are more than 100px apart at every size, so the targets
                never overlap. A tenth-opacity fill on hover and focus is the
                only thing drawn: the artwork already marks these three places,
                and anything more would be a second ring over the first — the
                mistake the ping itself made.

                Hidden under prefers-reduced-motion for the same reason as the
                dots: with the dial parked on one frame there is nothing for a
                click to change. */}
            {RANGE_VIEWS.map((view, i) => (
              <button
                key={`${view.id}-target`}
                type="button"
                onClick={() => pick(view)}
                aria-label={`Radar target ${i + 1} of ${RANGE_VIEWS.length} — show its range view`}
                style={{ left: `${view.x}%`, top: `${view.y}%` }}
                className="range-target absolute z-[6] block size-11 -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full bg-transparent hover:bg-primary/10 focus-visible:bg-primary/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white motion-reduce:hidden"
              />
            ))}
          </div>
        </div>
      </div>

      {/* This stylesheet has to live HERE, at the root of the component, not down
          in the radar column with the layers it mostly drives. styled-jsx scopes a
          block by stamping a hash class onto the JSX subtree the <style> element
          sits in, and nothing outside it: with the block inside the radar column
          the three `.range-photo` rules matched nothing at all — no animation and
          not even the resting `opacity: 0` — so all three photographs sat on top of
          one another at full opacity and the swap looked like one fixed photo. From
          the common ancestor both columns are in scope.

          It also has to stay STATIC — no interpolation anywhere in it. styled-jsx
          cannot analyse an interpolated block: a first pass wrote the durations and
          keyframe stops as expressions and the result was animation-duration 0s on
          the moving layer and all three photo rules dropped on the floor. So the
          one number that has to vary, the period, arrives as a custom property, and
          the keyframe percentages are literals with the arithmetic that produced
          them written beside each.

          Every stop is a share of one revolution, so degrees convert by dividing
          by 3.6 and seconds by dividing by 0.2. All five animations run the same
          duration, which is what locks the turn, the glows, the pings and the
          photographs together with no timer to drift. */}
      <style jsx>{`
        /* One clockwise revolution per period, linear, about SPIN. Nothing
           else needed: what turns is the artwork's own line, so it needs no
           gradient and no mask of its own. */
        .range-turn {
          animation-name: range-turn-spin;
          animation-duration: var(--range-period);
          animation-timing-function: linear;
          animation-iteration-count: infinite;
          will-change: transform;
        }
        @keyframes range-turn-spin {
          to {
            transform: rotate(360deg);
          }
        }

        /* A ring is lit for exactly as long as the line's red piece is over
           it, which is that piece's own width: LEAD_ARC 58.15 / 3.6 =
           16.153%. That is under the 74.09deg gap between the two closest
           rings, so one ring is lit at a time — the build script throws if
           that ever stops being true. Each ring slides the window onto its
           own bearing with a negative delay, so one set of keyframes serves
           all three and none of them wraps past 100%.

           Resting opacity lives here rather than on a utility class or an
           inline style: a running animation outranks this declaration, and
           keeping it in one place is what lets the reduced-motion block
           below light exactly one ring. */
        .range-hit {
          opacity: 0;
          animation-name: range-hit;
          animation-duration: var(--range-period);
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }
        @keyframes range-hit {
          0%,
          15.153% {
            opacity: 1;
          }
          16.153%,
          100% {
            opacity: 0;
          }
        }

        /* The core flickers twice as the line arrives and then holds lit for
           the rest of the window: 0.156s, 0.39s and 0.624s into the pass are
           0.78%, 1.95% and 3.12% of a revolution. Double rather than a single
           pulse on purpose — it reads as a signal being acquired, not as a CSS
           fade. Past 3.12% it stays at 1 because the wrapper's own range-hit
           window is what takes it away again at 16.153%. */
        .range-core {
          animation-name: range-core;
          animation-duration: var(--range-period);
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
        }
        @keyframes range-core {
          0% {
            opacity: 0.22;
          }
          0.78% {
            opacity: 1;
          }
          1.95% {
            opacity: 0.28;
          }
          3.12%,
          100% {
            opacity: 1;
          }
        }

        /* One photo per ring, held from the red piece reaching that ring until
           it reaches the next, in RANGE_VIEWS order: overview, cabinet, grid.
           Those rings are 158.919, 74.089 and 126.992 degrees apart, so the
           spans are 44.144%, 20.580% and 35.276% — they add to exactly 100%,
           which is why the hand-off is a swap and never a gap or a double
           exposure. The 0.4% ramp is 0.08s at a 20s period: a cut rather than
           a cross-fade. */
        .range-photo {
          opacity: 0;
        }
        .range-photo-0,
        .range-photo-1,
        .range-photo-2 {
          animation-duration: var(--range-period);
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }
        .range-photo-0 {
          animation-name: range-photo-0;
        }
        .range-photo-1 {
          animation-name: range-photo-1;
        }
        .range-photo-2 {
          animation-name: range-photo-2;
        }
        /* overview: 158.919 / 3.6 = 44.144 */
        @keyframes range-photo-0 {
          0%,
          43.744% {
            opacity: 1;
          }
          44.144%,
          99.6% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        /* cabinet: 74.089 / 3.6 = 20.580 */
        @keyframes range-photo-1 {
          0%,
          20.180% {
            opacity: 1;
          }
          20.580%,
          99.6% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        /* grid: 126.992 / 3.6 = 35.276 */
        @keyframes range-photo-2 {
          0%,
          34.876% {
            opacity: 1;
          }
          35.276%,
          99.6% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }

        /* One dot per photograph, holding for the same span on the same clock,
           so a dot is wide and red for exactly as long as its picture is up
           without anything having to keep the two in step. The stops are the
           photo stops; only the ramp differs — 1.5% is 0.3s at a 20s period,
           which is the 300ms the HackGrid dots ease over, where the photos
           themselves cut in 0.08s. Colours are literals because this block
           cannot interpolate: #ec1938 is --color-primary and #737373 is the
           resting bg-neutral-500. */
        .range-dot-0,
        .range-dot-1,
        .range-dot-2 {
          animation-duration: var(--range-period);
          animation-timing-function: ease-out;
          animation-iteration-count: infinite;
        }
        .range-dot-0 {
          animation-name: range-dot-0;
        }
        .range-dot-1 {
          animation-name: range-dot-1;
        }
        .range-dot-2 {
          animation-name: range-dot-2;
        }
        /* overview: current for 44.144% */
        @keyframes range-dot-0 {
          0%,
          42.644% {
            width: 24px;
            background-color: #ec1938;
          }
          44.144%,
          98.5% {
            width: 8px;
            background-color: #737373;
          }
          100% {
            width: 24px;
            background-color: #ec1938;
          }
        }
        /* cabinet: current for 20.580% */
        @keyframes range-dot-1 {
          0%,
          19.08% {
            width: 24px;
            background-color: #ec1938;
          }
          20.58%,
          98.5% {
            width: 8px;
            background-color: #737373;
          }
          100% {
            width: 24px;
            background-color: #ec1938;
          }
        }
        /* grid: current for 35.276% */
        @keyframes range-dot-2 {
          0%,
          33.776% {
            width: 24px;
            background-color: #ec1938;
          }
          35.276%,
          98.5% {
            width: 8px;
            background-color: #737373;
          }
          100% {
            width: 24px;
            background-color: #ec1938;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          /* Park the whole dial as one coherent still frame. Stopping the
             animations is not enough on its own — every layer falls back to
             its resting opacity, which would leave no ring lit and no photo
             at all — so one of each is switched back on. Which one is not a
             choice: rotate(0) is the line where the artwork draws it, its red
             piece covering 36.02..94.17, and the only ring inside that span
             is the grid one at 59.97, index 2. The ping rides the same
             range-hit window, so that same ring is the only one keeping its
             core, parked lit — exactly the frame it would be in mid-pass. The
             dot row is hidden outright here (see the note on it), so there is
             nothing to park it on: one frame is not a sequence. */
          .range-turn,
          .range-hit,
          .range-core,
          .range-photo-0,
          .range-photo-1,
          .range-photo-2,
          .range-dot-0,
          .range-dot-1,
          .range-dot-2 {
            animation: none;
          }
          .range-hit-2,
          .range-photo-2,
          .range-core {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

const OTCyberRange = () => {
  return (
    <section className="relative w-full px-4 sm:px-6 lg:px-8 pb-16 pt-2">
      {/* 1300px content box — same as the OT Security header (layout.tsx) and the
          AID ICS Framework page, so the body copy lines up with the page title's
          red bar and the first tab instead of sitting 10px further in. */}
      <div className="max-w-[1300px] w-full mx-auto relative z-10">
        <div className="flex flex-col gap-14 lg:gap-16">
          {/* Intro — copy and photo thumbnails left, image stage right. */}
          <ScrollReveal direction="up" delay={100}>
            <RangeIntro />
          </ScrollReveal>

          {/* Testbed */}
          <ScrollReveal direction="up" delay={200}>
            <OverviewSection data={testbed} />
          </ScrollReveal>

          {/* OT Lab */}
          <ScrollReveal direction="up" delay={250}>
            <OverviewSection data={otLab} />
          </ScrollReveal>

          {/* Key Features */}
          <ScrollReveal direction="up" delay={300}>
            <div className="flex flex-col gap-6">
              <BarHeading>Key Features</BarHeading>

              <OTKeyFeatures />
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
};

export default OTCyberRange;
